import React, { useEffect } from 'react';
import styles from './ModalFilePreview.module.scss';
import { ModalComponent } from 'new/routes/RouterManager';
import { ModalOutProps } from 'new/routes/ModalDefault';
import { Typography } from '@mui/material';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { PROBE_GET_FILE_REQUEST } from 'js/constants/actionTypes';
import { useProbeFile } from 'new/hooks/adapters/useProbeFile';
import { bytesToString } from 'js/lib/FormatUtils';
import LoadableMonacoEditor from 'js/components/elements/editors/monaco/LoadableMonacoEditor';
import { getEditorLanguage } from 'js/components/elements/editors/monaco/MonacoEditorUtils';
import { useResourceMeta } from 'new/hooks/adapters/useResourceMeta';
import { useResource } from 'new/components/monitor/ResourceTriage/useResource';
import { useDispatch } from 'react-redux';
import { probeGetFile } from 'js/actions';
import { useFilePath } from 'new/hooks/adapters/useFilePath';
import { CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT } from 'js/constants/connectionCategories';
import { Stack } from 'new/elements/Stack';

export interface ModalFilePreviewProps {
  fileName: string;
  displayFileName: string;
  filePath?: string;
  size: number;
  fileId: number;
  resourceId: string;
  resourceType: string;
  credentialId: number;
  noConnection?: boolean;
}

export const ModalFilePreview: ModalComponent = (props: ModalFilePreviewProps & ModalOutProps) => {
  const {
    fileName,
    displayFileName,
    size,
    setLoading,
    credentialId,
    noConnection,
    filePath,
    resourceId,
    resourceType,
  } = props;

  const dispatch = useDispatch();
  const { resource } = useResource(resourceType, resourceId);

  const meta = useResourceMeta(resourceType, resourceId, resource?.data_credentials);

  const type = resource?.source_type ?? resource?.sink_type;
  const config = resource?.source_config ?? resource?.sink_config;
  const loading = useIsLoading(PROBE_GET_FILE_REQUEST);
  const path = useFilePath(fileName, type, config);

  const probeFileData = useProbeFile({
    filePath: filePath || path,
    resourceId,
    resourceType,
    credentialId: credentialId || meta?.credential?.id,
    connectionCategory: noConnection ? null : meta?.connectionCategory,
  });

  const shouldFetchOnResource =
    !!meta?.connectionCategory &&
    meta.connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT;

  const { response, contentType, statusCode, errorMessage } = probeFileData || {};

  useEffect(() => {
    dispatch(
      probeGetFile(
        credentialId || meta?.credential?.id,
        { name: fileName, path: filePath || path, id: filePath || path },
        noConnection ? null : meta?.connectionCategory,
        shouldFetchOnResource,
        resourceType,
        resourceId,
      ),
    );
  }, []);

  useEffect(() => {
    setLoading(loading);
  }, [loading]);

  let mode = 'text';
  let height = window.innerHeight - 250;
  let editorContent = (() => {
    if (!loading) {
      if (contentType === 'application/json') {
        mode = 'json';
      }
      if (contentType === 'application/binary') {
        return 'This file contains binary data. File preview is not supported for binary data files.';
      }
      if (statusCode >= 400) {
        return `There was an error in opening the file.\nReason: ${errorMessage}`;
      }
      if (!response) {
        return 'This is an empty file.';
      }
    }
    return response;
  })();

  const editorLanguage = getEditorLanguage(mode);
  const finalFileName = displayFileName || fileName || '';

  return (
    <div aria-label="modal-file-preview-container">
      <div className={styles.info}>
        <Typography variant="subtitle2">File: {finalFileName}</Typography>
        <Typography variant="body3">Size: {bytesToString(size)}</Typography>
      </div>
      <Stack className={styles.stackEditor}>
        <LoadableMonacoEditor
          width="100%"
          height={height - 50}
          value={editorContent}
          language={editorLanguage}
          options={loadableMonacoEditorOptions}
        />
      </Stack>
    </div>
  );
};

const loadableMonacoEditorOptions = {
  readOnly: true,
  minimap: { enabled: false },
};

ModalFilePreview.modalConfig = {
  name: 'ModalFilePreview',
  title: 'File preview',
  maxWidth: 'lg',
};
